import React, { forwardRef, useCallback, useId, useMemo, useState } from 'react';
import Metric from './subcomponents/Metric';
import HeaderText from './subcomponents/CardHeader';
import Caret from './Icons/Caret';
import SkeletonBody from './Skeleton/SkeletonBody';
import SkeletonMetrics from './Skeleton/SkeletonMetrics';
import EmptyState from './subcomponents/EmptyCard';
import clsx from 'clsx';

const DrilldownCard = forwardRef(function DrilldownCard(
  {
    title,
    subtitle,
    metrics = {}, // { key: { formatted, delta, ... } }
    defaultExpanded = false,
    expanded,
    onExpandChange,
    loading = false,
    disabled = false,
    selected = false,
    children,
    ariaTitleId,
    keepMounted = false,
    className,
    style,
    shouldExpand = true,
    showArrow = false,
    onClickDaily = () => {},

    // 👇 NUEVO: props para "View Details"
    showViewDetails = false,
    viewDetailsLabel = 'View Details',
    onViewDetails,
    viewDetailsHref,
    viewDetailsAriaLabel,

    'data-testid': testId,
  },
  ref,
) {
  const isControlled = typeof expanded === 'boolean';
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;

  const autoHeaderId = useId();
  const headerId = ariaTitleId ?? autoHeaderId;
  const regionId = useId();

  const canInteract = !disabled && !loading;

  const setExpanded = useCallback(
    (next) => {
      if (!isControlled) setInternalExpanded(next);
      onExpandChange?.(next);
    },
    [isControlled, onExpandChange],
  );

  const toggle = useCallback(() => {
    if (!canInteract) return;
    setExpanded(!isExpanded);
  }, [canInteract, isExpanded, setExpanded]);

  const metricKey = useCallback((property, i) => `${property}-${i}`, []);

  const headerClasses = useMemo(
    () =>
      clsx(
        // 👇 habilitamos wrap para que "View Details" suba a la primera fila cuando no entra
        'flex w-full flex-wrap items-center gap-4 px-4 py-3 sm:px-6 sm:py-4',
        canInteract ? 'cursor-pointer' : 'cursor-default',
        'select-none',
      ),
    [canInteract],
  );

  const metricEntries =
    metrics && typeof metrics === 'object' && !Array.isArray(metrics)
      ? Object.entries(metrics)
      : [];

  return (
    <section
      ref={ref}
      data-selected={selected || undefined}
      aria-busy={loading || undefined}
      data-disabled={disabled || undefined}
      className={clsx(
        'w-full rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow pb-4 pt-4',
        canInteract && 'hover:shadow',
        disabled && 'opacity-60',
        selected && 'ring-2 ring-blue-500',
        className,
      )}
      style={style}
      data-testid={testId}
    >
      {/* Header */}
      <div className={headerClasses}>
        {/* Botón de toggle (ocupa el ancho disponible) */}
        <button
          type="button"
          className={clsx(
            'order-1 flex min-w-0 flex-1 items-center gap-4 text-left outline-none',
            !canInteract && 'pointer-events-none',
          )}
          aria-expanded={isExpanded}
          aria-controls={regionId}
          id={headerId}
          onClick={toggle}
          onKeyDown={(e) => {
            if (!canInteract) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
        >
          {showArrow && <Caret expanded={isExpanded} />}

          {/* Title + Subtitle */}
          <div className="min-w-0 flex-1">
            {loading ? (
              <div className="space-y-2">
                <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                {subtitle && <div className="h-3 w-32 animate-pulse rounded bg-slate-100" />}
              </div>
            ) : (
              <HeaderText title={title} subtitle={subtitle} />
            )}
          </div>

          {/* Métricas en header (solo ≥ xl) */}
          <div className="hidden xl:flex xl:flex-wrap items-center gap-x-6 gap-y-2">
            {loading ? (
              <SkeletonMetrics />
            ) : (
              metricEntries.map(([property, value], i) => (
                <Metric key={metricKey(property, i)} property={property} value={value} />
              ))
            )}
          </div>
        </button>

        {/* CTA "View Details" — fuera del botón para no togglear. 
            Con wrap: queda a la derecha del título en la 1ª fila si no entra todo. */}
        {showViewDetails && (
          <div className="order-1 ml-auto shrink-0">
            {viewDetailsHref ? (
              <a
                href={viewDetailsHref}
                aria-label={viewDetailsAriaLabel || viewDetailsLabel}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                {viewDetailsLabel}
                <ChevronRight className="h-4 w-4" />
              </a>
            ) : (
              <button
                type="button"
                aria-label={viewDetailsAriaLabel || viewDetailsLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDaily();
                }}
                onKeyDown={(e) => {
                  if (!canInteract) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClickDaily();
                  }
                }}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                {viewDetailsLabel}
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Métricas fuera del header (mobile + tablet < xl) */}
      {!loading && metricEntries.length > 0 && (
        <div className="px-4 pb-3 sm:px-6 xl:hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
            {metricEntries.map(([property, value], i) => (
              <Metric key={metricKey(property, i)} property={property} value={value} />
            ))}
          </div>
        </div>
      )}

      {/* Región expandible */}
      <div
        id={regionId}
        role="region"
        aria-labelledby={headerId}
        aria-hidden={!isExpanded}
        className={clsx(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          {(isExpanded || keepMounted) && shouldExpand && (
            <div className={clsx('px-4 pb-4 sm:px-6', !isExpanded && 'invisible m-0 h-0 p-0')}>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 sm:p-5">
                {loading ? <SkeletonBody /> : children ?? <EmptyState />}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

export default DrilldownCard;

/* -------------------- Iconito flecha -> -------------------- */
function ChevronRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M7.21 14.77a.75.75 0 0 1-1.06-1.06L9.44 10 6.15 6.29A.75.75 0 0 1 7.2 5.23l4.25 4.25c.29.3.29.77 0 1.06l-4.25 4.23Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
